import {child, DatabaseReference, endBefore, equalTo, get, getDatabase, limitToFirst, limitToLast, orderByKey, push, query, ref, set, startAfter} from "firebase/database";
import type {BasePayers, BasePlaces, PlannedPlaces} from "./index";
import {EstimationModel, ExpenditureModel, ExpenseModel, firebaseApp, ItineraryModel, PayerModel, PlaceModel, TripModel} from "./index";


const rootDbRef = ref(getDatabase(firebaseApp));

const TRIPS_NODE = "TRIPS";
const tripsNodeRef: DatabaseReference = child(rootDbRef, TRIPS_NODE);

const ITINERARIES_NODE = "ITINERARIES";
const itinerariesNodeRef: DatabaseReference = child(rootDbRef, ITINERARIES_NODE);

const EXPENDITURES_NODE = "EXPENDITURES";
const expendituresNodeRef: DatabaseReference = child(rootDbRef, EXPENDITURES_NODE);

const ESTIMATIONS_NODE = "ESTIMATIONS";
const estimationsNodeRef: DatabaseReference = child(rootDbRef, ESTIMATIONS_NODE);


//********************************************************************************
//* GENERATE KEY
//********************************************************************************


export const generateMainFirebaseRtdbKey = (): string => {
	  return push(tripsNodeRef).key;
};

export const generateStringKeyFromMillis = (): string => {
	  return Date.now().toString();
};

export const generatePayerKeyFromPayerName = (name: string): string => {
	  return name.replace(/[^a-zA-Z0-9]/gi, "").toLowerCase();
};


//********************************************************************************
//* TRIPS
//********************************************************************************


export const createTripsHookQuery = (uid: string) => {
	  return query(child(tripsNodeRef, uid), orderByKey());
};

export const addTrip = (uid: string, tripKey: string, trip: TripModel) => {
	  const pathRef = child(tripsNodeRef, `${uid}/${tripKey}`);
	  set(pathRef, trip)
				 .then(
							() => {
							},
							(error) => {
								  console.error("Error adding TripModel: ", error);
							}
				 );
};


//********************************************************************************
//* ITINERARIES
//********************************************************************************


export const createItinerariesHookQuery = (uid: string, key: string) => {
	  return query(child(itinerariesNodeRef, uid), orderByKey(), equalTo(key));
};

export const addItinerary = (uid: string, itineraryKey: string, itinerary: ItineraryModel) => {
	  const pathRef = child(itinerariesNodeRef, `${uid}/${itineraryKey}`);
	  set(pathRef, itinerary)
				 .then(
							() => {
							},
							(error) => {
								  console.error("Error adding ItineraryModel: ", error);
							}
				 );
};


export const addPlace = (uid: string, itineraryKey: string, anchoredOnDate: number | null, placeKey: string, place: PlaceModel) => {
	  const pathRef = anchoredOnDate ?
				 child(itinerariesNodeRef, `${uid}/${itineraryKey}/plannedPlaces/${anchoredOnDate.toString()}/${placeKey}`)
				 :
				 child(itinerariesNodeRef, `${uid}/${itineraryKey}/unassignedPlaces/${placeKey}`);
	  set(pathRef, place)
				 .then(
							() => {
							},
							(error) => {
								  console.error("Error adding PlaceModel: ", error);
							}
				 );
};

export const moveAllPlannedPlacesToUnassigned = (uid: string, itineraryKey: string) => {
	  const queryRef = query(child(itinerariesNodeRef, uid), orderByKey(), equalTo(itineraryKey));
	  get(queryRef)
				 .then(
							(snapshots) => {
								  if (snapshots.exists()) {
										 snapshots.forEach((snapshot) => {
												const itinerary = ItineraryModel.fromObject(snapshot.val());
												
												const plannedPlaces: PlannedPlaces = itinerary.plannedPlaces || {};
												const unassignedPlaces: BasePlaces = itinerary.unassignedPlaces || {};
												
												Object.keys(plannedPlaces).forEach((anchoredOnDate) => {
													  const basePlaces: BasePlaces = plannedPlaces[anchoredOnDate] || {};
													  Object.keys(basePlaces).forEach((placeKey) => {
															 const place: PlaceModel = PlaceModel.fromObject(basePlaces[placeKey]);
															 place.setAnchoredOnDate(null);
															 if (place.isTransport) place.closedOnDays = {};
															 set(child(itinerariesNodeRef, `${uid}/${itineraryKey}/unassignedPlaces/${placeKey}`), place)
																		.then(
																				  () => {
																				  },
																				  (error) => {
																						 console.error("Error moving PlaceModel to unassigned: ", error);
																				  }
																		);
															 set(child(itinerariesNodeRef, `${uid}/${itineraryKey}/plannedPlaces/${anchoredOnDate}/${placeKey}`), null)
																		.then(
																				  () => {
																				  },
																				  (error) => {
																						 console.error("Error removing PlaceModel from planned: ", error);
																				  }
																		);
													  });
												});
										 });
								  }
							},
							(error) => {
								  console.error("Error moving all planned Places to unassigned: ", error);
							}
				 );
};

export const setAllPlacesAsFulfilled = (uid: string, itineraryKey: string) => {
	  const queryRef = query(child(itinerariesNodeRef, uid), orderByKey(), equalTo(itineraryKey));
	  get(queryRef)
				 .then(
							(snapshots) => {
								  if (snapshots.exists()) {
										 snapshots.forEach((snapshot) => {
												const itinerary = ItineraryModel.fromObject(snapshot.val());
												
												const unassignedPlaces: BasePlaces = itinerary.unassignedPlaces || {};
												const plannedPlaces: PlannedPlaces = itinerary.plannedPlaces || {};
												
												Object.keys(unassignedPlaces).forEach((placeKey) => {
													  set(child(itinerariesNodeRef, `${uid}/${itineraryKey}/unassignedPlaces/${placeKey}/isFulfilled`), true)
																 .then(
																			() => {
																			},
																			(error) => {
																				  console.error("Error marking PlaceModel as fulfilled: ", error);
																			}
																 );
												});
												
												Object.keys(plannedPlaces).forEach((anchoredOnDate) => {
													  const basePlaces: BasePlaces = plannedPlaces[anchoredOnDate] || {};
													  Object.keys(basePlaces).forEach((placeKey) => {
															 set(child(itinerariesNodeRef, `${uid}/${itineraryKey}/plannedPlaces/${anchoredOnDate}/${placeKey}/isFulfilled`), true)
																		.then(
																				  () => {
																				  },
																				  (error) => {
																						 console.error("Error marking PlaceModel as fulfilled: ", error);
																				  }
																		);
													  });
												});
										 });
								  }
							},
							(error) => {
								  console.error("Error setting all Places as fulfilled: ", error);
							}
				 );
};

export const swapOrderOfPlannedPlaces = (uid: string, itineraryKey: string, anchoredOnDate: number, swapWithPrevious: boolean, thisPlaceKey: string, thisPlace: PlaceModel) => {
	  const pathRef = child(itinerariesNodeRef, `${uid}/${itineraryKey}/plannedPlaces/${anchoredOnDate.toString()}`);
	  const queryRef = swapWithPrevious ?
				 query(pathRef, orderByKey(), endBefore(thisPlaceKey), limitToLast(1))
				 :
				 query(pathRef, orderByKey(), startAfter(thisPlaceKey), limitToFirst(1));
	  get(queryRef)
				 .then(
							(snapshots) => {
								  if (snapshots.exists()) {
										 snapshots.forEach((snapshot) => {
												const otherPlace = PlaceModel.fromObject(snapshot.val());
												
												if (otherPlace.isFulfilled) return;
												
												const temp = otherPlace.placeKey;
												
												otherPlace.placeKey = thisPlaceKey;
												thisPlace.placeKey = temp;
												
												set(child(pathRef, otherPlace.placeKey), otherPlace)
														  .then(
																	 () => {
																	 },
																	 (error) => {
																			console.error("Error swapping order of Places: ", error);
																	 }
														  );
												
												set(child(pathRef, thisPlace.placeKey), thisPlace)
														  .then(
																	 () => {
																	 },
																	 (error) => {
																			console.error("Error swapping order of Places: ", error);
																	 }
														  );
										 });
								  }
							},
							(error) => {
								  console.error("Error swapping order of Places: ", error);
							}
				 );
};


//********************************************************************************
//* EXPENDITURES
//********************************************************************************


export const createExpendituresHookQuery = (uid: string, key: string) => {
	  return query(child(expendituresNodeRef, uid), orderByKey(), equalTo(key));
};

export const addExpenditure = (uid: string, expenditureKey: string, expenditure: ExpenditureModel) => {
	  const pathRef = child(expendituresNodeRef, `${uid}/${expenditureKey}`);
	  set(pathRef, expenditure)
				 .then(
							() => {
							},
							(error) => {
								  console.error("Error adding ExpenditureModel: ", error);
							}
				 );
};

export const addExpense = (uid: string, expenditureKey: string, expenseKey: string, expense: ExpenseModel) => {
	  const pathRef = child(expendituresNodeRef, `${uid}/${expenditureKey}/expenses/${expenseKey}`);
	  set(pathRef, expense)
				 .then(
							() => {
							},
							(error) => {
								  console.error("Error adding ExpenseModel: ", error);
							}
				 );
};

export const updatePayers = (uid: string, expenditureKey: string, payers: BasePayers) => {
	  const pathRef = child(expendituresNodeRef, `${uid}/${expenditureKey}/payers`);
	  set(pathRef, payers)
				 .then(
							() => {
							},
							(error) => {
								  console.error("Error updating Payers: ", error);
							}
				 );
};

export const addPayer = (uid: string, expenditureKey: string, payerKey: string, payer: PayerModel) => {
	  const pathRef = child(expendituresNodeRef, `${uid}/${expenditureKey}/payers/${payerKey}`);
	  set(pathRef, payer)
				 .then(
							() => {
							},
							(error) => {
								  console.error("Error adding PayerModel: ", error);
							}
				 );
};


//********************************************************************************
//* ESTIMATIONS
//********************************************************************************


export const createEstimationsHookQuery = (uid: string) => {
	  return query(child(estimationsNodeRef, uid), orderByKey());
};

export const addEstimation = (uid: string, estimationKey: string, estimation: EstimationModel) => {
	  const pathRef = child(estimationsNodeRef, `${uid}/${estimationKey}`);
	  set(pathRef, estimation)
				 .then(
							() => {
							},
							(error) => {
								  console.error("Error adding EstimationModel: ", error);
							}
				 );
};


//********************************************************************************
//* ALL NODES FOR DELETION
//********************************************************************************


export const nodeRefs: DatabaseReference[] = [
	  tripsNodeRef,
	  itinerariesNodeRef,
	  expendituresNodeRef,
	  estimationsNodeRef
];